import { useEffect, useMemo, useState } from "react";
import { X, Banknote, CreditCard, Landmark, Ticket, Layers, QrCode } from "lucide-react";
import { PaymentMethod } from "@finora/types";
import { formatMoney, PAYMENT_METHOD_LABELS } from "@finora/config";
import { Button } from "@finora/ui";
import type { FiscalDocumentDto } from "@finora/types";

const METHOD_ICONS: Record<PaymentMethod, typeof Banknote> = {
  [PaymentMethod.CASH]: Banknote,
  [PaymentMethod.CARD]: CreditCard,
  [PaymentMethod.BANK_TRANSFER]: Landmark,
  [PaymentMethod.VOUCHER]: Ticket,
  [PaymentMethod.COMBINED]: Layers,
  [PaymentMethod.INSTANT_PAYMENT]: QrCode,
  [PaymentMethod.CHECK]: Landmark,
  [PaymentMethod.OTHER]: Layers,
};

const SPLITTABLE_METHODS = [
  PaymentMethod.CASH,
  PaymentMethod.CARD,
  PaymentMethod.BANK_TRANSFER,
  PaymentMethod.VOUCHER,
  PaymentMethod.INSTANT_PAYMENT,
  PaymentMethod.CHECK,
  PaymentMethod.OTHER,
] as const;

export interface CheckoutPaymentEntry {
  method: PaymentMethod;
  amount: string;
  receivedAmount?: string;
}

export interface CheckoutModalProps {
  document: FiscalDocumentDto;
  isSubmitting: boolean;
  errorMessage?: string | null;
  defaultCombinedMethod?: PaymentMethod;
  onClose: () => void;
  onConfirm: (payments: CheckoutPaymentEntry[]) => void;
  // Ukupan popust na račun (24.09.2026, korisnički zahtev) - kasir ga sad
  // unosi ovde, na naplati, ne pre toga u korpi, U PROCENTIMA (konverzija u
  // apsolutan iznos je unutar ove komponente - backend polje
  // `discountAmount` na PATCH-u ostaje apsolutan iznos, isto kao pre).
  onUpdateDiscount: (discountAmount: string) => void;
}

export function CheckoutModal({
  document: doc,
  isSubmitting,
  errorMessage,
  defaultCombinedMethod = PaymentMethod.CASH,
  onClose,
  onConfirm,
  onUpdateDiscount,
}: CheckoutModalProps) {
  // Doplata (razgovor 23.09.2026): za dokument nastao zatvaranjem avansa
  // (doc.advanceCreditAmount, računsko polje - vidi
  // FiscalDocumentsService.get()), kasa traži naplatu SAMO razlike do
  // pune vrednosti prometa, ne ceo iznos - za svaki običan dokument
  // (advanceCreditAmount prazno) amountDue === doc.grossAmount, identično
  // kao pre ove izmene.
  const advanceCredit = Number(doc.advanceCreditAmount ?? "0");
  const amountDue = Math.max(Number(doc.grossAmount) - advanceCredit, 0).toFixed(2);

  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [receivedInput, setReceivedInput] = useState(amountDue);
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({});

  // Popust na račun se UNOSI U PROCENTIMA (24.09.2026, korisnički zahtev),
  // osnovica je ukupan bruto iznos stavki PRE ovog popusta (stavke već
  // uključuju SVOJ eventualni popust po stavci, ali ne i ovaj) - ista
  // osnovica koju calculateDocumentTotals() koristi za proporcionalnu
  // raspodelu. Round-trip ide preko `doc.documentDiscountAmount` (sirova
  // vrednost, NE `doc.discountAmount` koji je zbir sa popustima po
  // stavkama - vidi napomenu uz to polje u @finora/types).
  const preDiscountTotal = useMemo(
    () => doc.items.reduce((sum, item) => sum + Number(item.grossAmount), 0),
    [doc.items],
  );
  const percentFromAmount = (amount: string) => {
    const amt = Number(amount);
    if (preDiscountTotal <= 0 || amt <= 0) return "";
    return (amt / preDiscountTotal) * 100;
  };
  const toDisplay = (pct: number | "") => (pct === "" ? "" : pct.toFixed(pct % 1 === 0 ? 0 : 2));

  const [discountDraft, setDiscountDraft] = useState(() =>
    toDisplay(percentFromAmount(doc.documentDiscountAmount ?? "0")),
  );

  // Popust menja doc.grossAmount (server preračunava totale), pa se ovde
  // resetuje "Primljeni iznos" na novi ukupan iznos - ako je kasir već
  // otkucao nešto na tastaturi PRE unosa popusta, to se gubi, ali to je
  // prihvatljivo jer se popust po dogovoru unosi PRE biranja iznosa uplate.
  useEffect(() => {
    setReceivedInput(amountDue);
  }, [amountDue]);

  useEffect(() => {
    setDiscountDraft(toDisplay(percentFromAmount(doc.documentDiscountAmount ?? "0")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.documentDiscountAmount, preDiscountTotal]);

  const change = useMemo(() => {
    const diff = Number(receivedInput || "0") - Number(amountDue);
    return diff > 0 ? diff.toFixed(2) : "0.00";
  }, [receivedInput, amountDue]);

  // Ostali načini (bez podrazumevanog) se unose ručno; podrazumevani
  // (obično gotovina) automatski dobija OSTATAK do ukupnog iznosa - ne
  // treba ga ručno kucati.
  const manualMethods = SPLITTABLE_METHODS.filter((m) => m !== defaultCombinedMethod);
  const manualTotal = useMemo(
    () => manualMethods.reduce((sum, m) => sum + Number(splitAmounts[m] || "0"), 0),
    [splitAmounts, manualMethods],
  );
  const defaultMethodAmount = Math.max(Number(amountDue) - manualTotal, 0).toFixed(2);

  const canConfirm =
    method === PaymentMethod.COMBINED
      ? manualTotal > 0 && Number(defaultMethodAmount) >= 0 && manualTotal <= Number(amountDue)
      : method !== PaymentMethod.CASH || Number(receivedInput || "0") >= Number(amountDue);

  function appendDigit(digit: string) {
    setReceivedInput((prev) => (prev === "0" ? digit : prev + digit));
  }

  function handleConfirm() {
    if (method === PaymentMethod.COMBINED) {
      const payments: CheckoutPaymentEntry[] = manualMethods
        .filter((m) => Number(splitAmounts[m] || "0") > 0)
        .map((m) => ({ method: m, amount: splitAmounts[m] || "0" }));
      if (Number(defaultMethodAmount) > 0) {
        payments.push({ method: defaultCombinedMethod, amount: defaultMethodAmount });
      }
      onConfirm(payments);
    } else {
      onConfirm([
        {
          method,
          amount: amountDue,
          receivedAmount: method === PaymentMethod.CASH ? receivedInput : undefined,
        },
      ]);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4">
      <div className="mx-auto my-6 flex w-full max-w-lg flex-col rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Naplata računa</h2>
          <button
            type="button"
            aria-label="Zatvori"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          {advanceCredit > 0 && (
            <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
              <div className="flex justify-between">
                <span>Ukupan promet</span>
                <span className="font-medium">{formatMoney(doc.grossAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span>Plaćeno avansom</span>
                <span className="font-medium">-{formatMoney(String(advanceCredit))}</span>
              </div>
            </div>
          )}
          <div className="rounded-lg bg-app-bg p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-slate-400">
              {advanceCredit > 0 ? "Preostalo za uplatu (doplata)" : "Ukupno za naplatu"}
            </p>
            <p className="text-3xl font-semibold text-slate-900">{formatMoney(amountDue)}</p>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Popust na račun</p>
            <div className="flex items-center gap-2">
              <input
                inputMode="decimal"
                value={discountDraft}
                onChange={(e) => setDiscountDraft(e.target.value)}
                onBlur={() => {
                  const pct = discountDraft.trim() === "" ? 0 : Number(discountDraft.replace(",", "."));
                  const clampedPct = Math.min(Math.max(Number.isFinite(pct) ? pct : 0, 0), 100);
                  const nextAmount = ((clampedPct / 100) * preDiscountTotal).toFixed(2);
                  if (nextAmount !== Number(doc.documentDiscountAmount ?? "0").toFixed(2)) {
                    onUpdateDiscount(nextAmount);
                  }
                }}
                placeholder="0"
                className="h-10 w-24 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
              <span className="text-sm text-slate-400">%</span>
              {Number(doc.documentDiscountAmount ?? "0") > 0 && (
                <span className="text-sm text-slate-400">
                  ({formatMoney(doc.documentDiscountAmount)})
                </span>
              )}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Način plaćanja</p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {Object.values(PaymentMethod).map((m) => {
                const Icon = METHOD_ICONS[m];
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    className={`flex h-16 flex-col items-center justify-center gap-1 rounded-lg border text-xs font-medium ${
                      method === m
                        ? "border-brand bg-brand-light text-brand"
                        : "border-border text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {PAYMENT_METHOD_LABELS[m]}
                  </button>
                );
              })}
            </div>
          </div>

          {method === PaymentMethod.CASH && (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Primljeni iznos</p>
              <input
                inputMode="decimal"
                value={receivedInput}
                onChange={(e) => setReceivedInput(e.target.value)}
                className="mb-3 h-12 w-full rounded-lg border border-border px-3 text-lg font-medium focus:border-brand focus:outline-none"
              />
              <div className="grid grid-cols-3 gap-2">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "0", "⌫"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      key === "⌫"
                        ? setReceivedInput((prev) => prev.slice(0, -1) || "0")
                        : appendDigit(key)
                    }
                    className="h-12 rounded-lg border border-border text-base font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                  >
                    {key}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex justify-between rounded-lg bg-emerald-50 px-4 py-3 text-sm">
                <span className="font-medium text-emerald-700">Kusur</span>
                <span className="font-semibold text-emerald-700">{formatMoney(change)}</span>
              </div>
            </div>
          )}

          {method === PaymentMethod.COMBINED && (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">
                Unesite iznos po svakom načinu plaćanja
              </p>
              <div className="space-y-2">
                {manualMethods.map((m) => {
                  const Icon = METHOD_ICONS[m];
                  return (
                    <div key={m} className="flex items-center gap-3">
                      <div className="flex w-36 items-center gap-2 text-sm text-slate-600">
                        <Icon className="h-4 w-4 text-slate-400" aria-hidden />
                        {PAYMENT_METHOD_LABELS[m]}
                      </div>
                      <input
                        inputMode="decimal"
                        value={splitAmounts[m] ?? ""}
                        onChange={(e) =>
                          setSplitAmounts((prev) => ({ ...prev, [m]: e.target.value }))
                        }
                        placeholder="0.00"
                        className="h-10 flex-1 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                      />
                    </div>
                  );
                })}
                <div className="flex items-center gap-3">
                  <div className="flex w-36 items-center gap-2 text-sm font-medium text-slate-700">
                    {(() => {
                      const Icon = METHOD_ICONS[defaultCombinedMethod];
                      return <Icon className="h-4 w-4 text-slate-400" aria-hidden />;
                    })()}
                    {PAYMENT_METHOD_LABELS[defaultCombinedMethod]}
                  </div>
                  <div className="flex h-10 flex-1 items-center rounded-lg border border-dashed border-border bg-app-bg px-3 text-sm font-medium text-slate-600">
                    {formatMoney(defaultMethodAmount)}
                  </div>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                {PAYMENT_METHOD_LABELS[defaultCombinedMethod]} automatski pokriva ostatak do ukupnog
                iznosa - podesivo u Podešavanjima.
              </p>
            </div>
          )}

          {errorMessage && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errorMessage}</p>
          )}
        </div>

        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
            Otkaži
          </Button>
          <Button
            variant="success"
            className="flex-1"
            disabled={!canConfirm || isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting ? "Fiskalizacija u toku..." : "Fiskalizuj i završi račun"}
          </Button>
        </div>
      </div>
    </div>
  );
}
